import { ChangeEvent, FC, useEffect, useState } from 'react';

import { capitalizeFirstLetter } from '@eneco/common/string';
import { emailValidation } from '@eneco/common/validation/emailRegex';
import AddressFinder, { addressFinderSchema } from '@eneco/components/AddressFinder/AddressFinder';
import { Header, Layout, NextButton } from '@eneco/flows/components';
import { formatInitials } from '@eneco/flows/helpers';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { Box, Grid, InputEmail, InputTelephone, InputText, RadioButton, RadioGroup } from '@eneco/sparky';
import { Customers_Address_AddressModel, DC_Domain_Models_Customers_Gender } from '@monorepo-types/dc';
import { usePlaceholderContent } from '@sitecore/common';
import { DeceasedFlowDeceasedContactPersonDetailsStepRendering } from '@sitecore/types/DeceasedFlowDeceasedContactPersonDetailsStep';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FlowContextDeceased } from '../../context';

const formatPhoneNumber = (phoneNumber: string): string => {
  // Remove all non-numeric characters
  let formattedNumber = phoneNumber.replace(/\D/g, '');
  // If the number starts with "31" (international code for Netherlands), replace it with "0"
  if (formattedNumber.startsWith('31')) {
    formattedNumber = '0' + formattedNumber.slice(2);
  }
  // If the number doesn't start with "0", add "0"
  if (!formattedNumber.startsWith('0')) {
    formattedNumber = '0' + formattedNumber;
  }
  // Format the number correctly if it is a mobile number starting with "06"
  if (formattedNumber.startsWith('06') && formattedNumber.length === 10) {
    return formattedNumber;
  }
  // Format the number correctly for other cases
  return formattedNumber.replace(/^(\d{3})(\d{7})$/, '$1-$2');
};

const convertToSalutation = (gender: string | undefined) => {
  let result;
  switch (gender) {
    case 'male' as DC_Domain_Models_Customers_Gender:
      result = 'dhr';
      break;
    case 'female' as DC_Domain_Models_Customers_Gender:
      result = 'mevr';
      break;
    default:
      result = '';
  }
  return result;
};

const convertToDcValues = (val: string) => {
  let result;
  switch (val) {
    case 'dhr':
      result = 'male';
      break;
    case 'mevr':
      result = 'female';
      break;
    default:
      result = 'unknown';
  }
  return result;
};

interface FormFields {
  gender: string;
  initials: string;
  firstName: string;
  surnamePreposition: string;
  surname: string;
  postalCode: string;
  houseNumber: string;
  houseNumberSuffix?: string;
  phoneNumber: string;
  emailAddress: string;
}

export const DeceasedContactPersonDetails: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContextDeceased>();
  const { send: sendToFlowMachine } = useFlowActorRef();
  const contactPerson = useFlowSelector(state => state.context.contactPerson);
  const [addressFinderData, setAddressFinderData] = useState<Customers_Address_AddressModel>();
  const [isHouseNumberSuffixRequired, setIsHouseNumberSuffixRequired] = useState(false);
  const { DeceasedFlowDeceasedContactPersonDetailsStep: { fields: textualData } = {} } = usePlaceholderContent<{
    DeceasedFlowDeceasedContactPersonDetailsStep: DeceasedFlowDeceasedContactPersonDetailsStepRendering;
  }>();

  // TODO - the phone number should check with libs/common/src/validation/phoneNumberRegex.ts and should be refactored
  const formSchema = yup.object({
    gender: yup.string().required(textualData?.personalDetails?.salutationRadio?.value?.requiredMessage),
    initials: yup.string().required(textualData?.personalDetails?.initialsFormField?.value?.requiredMessage),
    surnamePreposition: yup.string().optional(),
    surname: yup.string().required(textualData?.personalDetails?.surnameFormField?.value?.requiredMessage),
    phoneNumber: yup.string().required(textualData?.contactDetails?.phoneNumberFormField?.value?.requiredMessage),
    emailAddress: emailValidation({
      emailMessage: textualData?.contactDetails?.emailAddressFormField?.value?.validationMessage,
      requiredMessage: textualData?.contactDetails?.emailAddressFormField?.value?.requiredMessage,
    }),
    ...addressFinderSchema(isHouseNumberSuffixRequired).fields,
  });

  const formMethods = useForm<FormFields>({
    mode: 'onTouched',
    resolver: typedYupResolver<FormFields>(formSchema),
    defaultValues: {
      initials: contactPerson?.initials ?? '',
      surnamePreposition: contactPerson?.surnamePreposition ?? '',
      surname: contactPerson?.surname ?? '',
      phoneNumber: contactPerson?.phoneNumber ?? '',
      emailAddress: contactPerson?.emailAddress ?? '',
      houseNumber: contactPerson?.houseNumber.toString() ?? '',
      houseNumberSuffix: contactPerson?.houseNumberSuffix ?? '',
      postalCode: contactPerson?.postalCode ?? '',
      gender: convertToSalutation(contactPerson?.gender) ?? undefined,
    },
  });

  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    register,
    formState: { errors, isSubmitSuccessful },
  } = formMethods;

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();
      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          contactPerson: {
            gender: convertToDcValues(formData.gender) as DC_Domain_Models_Customers_Gender,
            initials: formData.initials,
            surnamePreposition: formData.surnamePreposition || undefined,
            surname: formData.surname,
            postalCode: addressFinderData?.postalCode ?? '',
            houseNumber: Number(addressFinderData?.houseNumber),
            houseNumberSuffix: addressFinderData?.houseNumberSuffix ?? '',
            street: addressFinderData?.street ?? '',
            city: addressFinderData?.city ?? '',
            phoneNumber: formatPhoneNumber(formData.phoneNumber),
            emailAddress: formData.emailAddress,
          },
        },
      });
    }
  }, [
    addressFinderData?.city,
    addressFinderData?.houseNumber,
    addressFinderData?.houseNumberSuffix,
    addressFinderData?.postalCode,
    addressFinderData?.street,
    getValues,
    isSubmitSuccessful,
    sendToFlowMachine,
  ]);

  return (
    <Layout.Content variant="B" handleSubmit={handleSubmit}>
      <Header>
        <Header.Title>{textualData?.content?.title?.value}</Header.Title>
        {textualData?.content?.text?.value && <Header.Text>{textualData.content.text.value}</Header.Text>}
      </Header>
      <FormProvider {...formMethods}>
        <Controller
          control={control}
          name="gender"
          render={({ field: { onChange, value, name } }) => (
            <RadioGroup
              label={textualData?.personalDetails?.salutationRadio?.value?.label ?? ''}
              name={name}
              direction="row"
              value={value}
              onValueChange={onChange}
              error={errors.gender?.message}>
              {textualData?.personalDetails?.salutationRadio?.value?.options?.map(option => (
                <RadioButton value={option.name} key={option.name}>
                  {option.label}
                </RadioButton>
              ))}
            </RadioGroup>
          )}
        />

        <Grid columns="2" columnGap="3">
          <InputText
            {...register('initials', {
              onBlur: (event: ChangeEvent<HTMLInputElement>) => {
                setValue('initials', formatInitials(event.target.value));
              },
            })}
            label={textualData?.personalDetails?.initialsFormField?.value?.label ?? ''}
            placeholder={textualData?.personalDetails?.initialsFormField?.value?.placeholder}
            hint={textualData?.personalDetails?.initialsFormField?.value?.hint}
            error={errors.initials?.message}
          />
          <InputText
            {...register('surnamePreposition')}
            label={textualData?.personalDetails?.surnamePrepositionFormField?.value?.label ?? ''}
            placeholder={textualData?.personalDetails?.surnamePrepositionFormField?.value?.placeholder}
            hint={textualData?.personalDetails?.surnamePrepositionFormField?.value?.hint}
            error={errors.surnamePreposition?.message}
          />
        </Grid>

        <InputText
          {...register('surname', {
            onBlur: (event: ChangeEvent<HTMLInputElement>) => {
              setValue('surname', capitalizeFirstLetter(event?.target?.value?.trim()));
            },
          })}
          label={textualData?.personalDetails?.surnameFormField?.value?.label ?? ''}
          placeholder={textualData?.personalDetails?.surnameFormField?.value?.placeholder}
          hint={textualData?.personalDetails?.surnameFormField?.value?.hint}
          error={errors.surname?.message}
        />

        <Box>
          <AddressFinder
            customLabels={{
              houseNumber: textualData?.address?.houseNumberFormField,
              postalCode: textualData?.address?.postalCodeFormField,
              houseNumberSuffix: textualData?.address?.houseNumberSuffixFormField,
            }}
            isSuffixRequired={isHouseNumberSuffixRequired}
            setIsSuffixRequired={setIsHouseNumberSuffixRequired}
            setAddressFinderData={setAddressFinderData}
          />
        </Box>

        <InputTelephone
          {...register('phoneNumber')}
          autoComplete="tel"
          label={textualData?.contactDetails?.phoneNumberFormField?.value?.label ?? ''}
          placeholder={textualData?.contactDetails?.phoneNumberFormField?.value?.placeholder}
          hint={textualData?.contactDetails?.phoneNumberFormField?.value?.hint}
          error={errors.phoneNumber?.message}
        />

        <InputEmail
          {...register('emailAddress')}
          autoComplete="email"
          label={textualData?.contactDetails?.emailAddressFormField?.value?.label ?? ''}
          placeholder={textualData?.contactDetails?.emailAddressFormField?.value?.placeholder}
          hint={textualData?.contactDetails?.emailAddressFormField?.value?.hint}
          error={errors.emailAddress?.message}
        />
      </FormProvider>
      <NextButton alignX="start" isLoading={isSubmitSuccessful}>
        {textualData?.content?.nextStepText?.value}
      </NextButton>
    </Layout.Content>
  );
};
